import type { ReactElement, ReactNode } from "react";
import { Navigate, Outlet } from "react-router-dom";

import { AuthPageLoading } from "@/components/layout/AuthPageShell";
import { AuthLoadError } from "@/components/shared/AuthLoadError";
import { useAuth } from "@/hooks/useAuth";
import {
  needsCollaboratorOnboarding,
  needsOrgAcceptInvite,
  resolvePostAuthRoute,
} from "@/lib/authRouting";

type RequireAuthProps = {
  children?: ReactNode;
};

export function RequireAuth({ children }: RequireAuthProps): ReactElement {
  const { user, isLoading, isError, refetch } = useAuth();

  if (isLoading) return <AuthPageLoading />;

  if (isError) return <AuthLoadError onRetry={refetch} />;

  if (!user) return <Navigate to="/login" replace />;
  if (!user.organization) return <Navigate to={resolvePostAuthRoute(user)} replace />;
  if (needsOrgAcceptInvite(user)) return <Navigate to={resolvePostAuthRoute(user)} replace />;
  if (needsCollaboratorOnboarding(user)) return <Navigate to="/onboarding" replace />;
  if (user.organization && user.isEnabled === false) {
    return <Navigate to="/member-disabled" replace />;
  }

  if (children != null) {
    return <>{children}</>;
  }

  return <Outlet />;
}
